import type { MfaChallenge, Tokens, User } from './types.ts';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type QueryValue = string | number | boolean | undefined | null;

export interface ApiRequest {
  method: HttpMethod;
  path: string;
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** json (default), text, base64 (binary as base64 string) or none. */
  responseType?: 'json' | 'text' | 'base64' | 'none';
}

export type StreamEventHandler = (event: string, data: any) => void;

/**
 * How a client reaches the API. Desktop renderers use an IPC transport (tokens stay in the main process);
 * the main process, iOS and tests use {@link FetchTransport} directly.
 */
export interface Transport {
  request<T>(req: ApiRequest): Promise<T>;
  /** Server-sent events; resolves when the stream ends. */
  stream(req: ApiRequest, onEvent: StreamEventHandler, signal?: AbortSignal): Promise<void>;
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

export interface Session {
  baseUrl: string;
  user: User;
  accessToken: string;
  /** epoch ms */
  accessExpiresAt: number;
  refreshToken: string;
}

/** Persists the session (Electron safeStorage, iOS Keychain, memory in tests). */
export interface SessionStore {
  load(): Promise<Session | null>;
  save(session: Session | null): Promise<void>;
}

/** Set by a self-hosted web loader (`window.__DTS_CONFIG__`) to point the app at that site's own server. */
export interface DtsRuntimeConfig {
  apiBaseUrl?: string;
}

const runtimeBaseUrl = (globalThis as { __DTS_CONFIG__?: DtsRuntimeConfig }).__DTS_CONFIG__?.apiBaseUrl?.trim();

export const DEFAULT_BASE_URL = runtimeBaseUrl ? runtimeBaseUrl.replace(/\/+$/, '') : 'https://dts-email-ingest.azurewebsites.net/api';

export function buildUrl(baseUrl: string, path: string, query?: Record<string, QueryValue>): string {
  const url = `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  if (!query) return url;
  const qs = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== false)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return qs ? `${url}?${qs}` : url;
}

function toSession(baseUrl: string, user: User, tokens: Tokens): Session {
  return {
    baseUrl,
    user,
    accessToken: tokens.accessToken,
    accessExpiresAt: Date.now() + tokens.expiresInSeconds * 1000,
    refreshToken: tokens.refreshToken,
  };
}

async function postJson<T>(fetchImpl: typeof fetch, baseUrl: string, path: string, body: unknown): Promise<T> {
  const res = await fetchImpl(buildUrl(baseUrl, path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as T;
}

async function errorFrom(res: Response): Promise<ApiError> {
  let message = `${res.status} ${res.statusText}`;
  try {
    const text = await res.text();
    if (text) {
      try {
        message = (JSON.parse(text) as { error?: string }).error ?? text;
      } catch {
        message = text.slice(0, 300);
      }
    }
  } catch {
    /* ignore */
  }
  return new ApiError(res.status, message);
}

function toBase64(bytes: Uint8Array): string {
  const B = (globalThis as any).Buffer;
  if (B) return B.from(bytes).toString('base64');
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/** Parses a text/event-stream body, invoking onEvent for each complete event. */
export async function readSse(body: ReadableStream<Uint8Array>, onEvent: StreamEventHandler): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let event = 'message';
  let data: string[] = [];
  const dispatch = () => {
    if (data.length === 0) return;
    const raw = data.join('\n');
    let parsed: unknown = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      /* plain text payload */
    }
    onEvent(event, parsed);
    event = 'message';
    data = [];
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl).replace(/\r$/, '');
      buffer = buffer.slice(nl + 1);
      if (line === '') dispatch();
      else if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
    }
  }
  dispatch();
}

/**
 * Talks to the API with fetch, attaching the bearer token and transparently refreshing it
 * (single-flight) shortly before expiry or after a 401.
 */
export class FetchTransport implements Transport {
  private readonly store: SessionStore;
  private readonly fetchImpl: typeof fetch;
  private readonly onSessionExpired?: () => void;
  private readonly extraHeaders: Record<string, string>;
  private refreshing: Promise<Session | null> | null = null;

  constructor(store: SessionStore, opts: { fetch?: typeof fetch; onSessionExpired?: () => void; headers?: Record<string, string> } = {}) {
    this.store = store;
    this.extraHeaders = opts.headers ?? {};
    this.fetchImpl = opts.fetch ?? fetch.bind(globalThis);
    this.onSessionExpired = opts.onSessionExpired;
  }

  /** First sign-in step. A correct password returns a two-step verification challenge, never a session. */
  static async login(baseUrl: string, email: string, password: string, fetchImpl: typeof fetch = fetch): Promise<MfaChallenge> {
    return (await postJson<{ mfa: MfaChallenge }>(fetchImpl, baseUrl, 'auth/login', { email, password })).mfa;
  }

  /** Accepts an invitation (sets the password with the one-time code); the new person then sets up two-step verification. */
  static async acceptInvite(baseUrl: string, email: string, code: string, password: string, fetchImpl: typeof fetch = fetch): Promise<MfaChallenge> {
    return (await postJson<{ mfa: MfaChallenge }>(fetchImpl, baseUrl, 'auth/accept-invite', { email, code, password })).mfa;
  }

  /** Second step: an authenticator code (or a recovery code) turns the challenge into a session. */
  static async verifyMfa(baseUrl: string, mfaToken: string, code: string, fetchImpl: typeof fetch = fetch): Promise<Session> {
    const body = await postJson<{ user: User; tokens: Tokens }>(fetchImpl, baseUrl, 'auth/mfa/verify', { mfaToken, code });
    return toSession(baseUrl, body.user, body.tokens);
  }

  /** Finishes first-time setup with a code from the scanned QR code; returns the session and one-time recovery codes. */
  static async enableMfa(baseUrl: string, mfaToken: string, code: string, fetchImpl: typeof fetch = fetch): Promise<{ session: Session; recoveryCodes: string[] }> {
    const body = await postJson<{ user: User; tokens: Tokens; recoveryCodes: string[] }>(fetchImpl, baseUrl, 'auth/mfa/enable', { mfaToken, code });
    return { session: toSession(baseUrl, body.user, body.tokens), recoveryCodes: body.recoveryCodes };
  }

  async logout(): Promise<void> {
    const s = await this.store.load();
    if (s) {
      try {
        await this.fetchImpl(buildUrl(s.baseUrl, 'auth/logout'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.accessToken}` },
          body: JSON.stringify({ refreshToken: s.refreshToken }),
        });
      } catch {
        /* offline logout still clears the local session */
      }
    }
    await this.store.save(null);
  }

  private async refresh(current: Session): Promise<Session | null> {
    if (!this.refreshing) {
      this.refreshing = (async () => {
        try {
          const res = await this.fetchImpl(buildUrl(current.baseUrl, 'auth/refresh'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: current.refreshToken }),
          });
          if (!res.ok) {
            if (res.status === 401 || res.status === 400 || res.status === 403) {
              await this.store.save(null);
              this.onSessionExpired?.();
            }
            return null;
          }
          const body = (await res.json()) as { user: User; tokens: Tokens };
          const next = toSession(current.baseUrl, body.user, body.tokens);
          await this.store.save(next);
          return next;
        } finally {
          setTimeout(() => (this.refreshing = null), 0);
        }
      })();
    }
    return this.refreshing;
  }

  private async session(): Promise<Session> {
    const s = await this.store.load();
    if (!s) throw new ApiError(401, 'Not signed in.');
    if (s.accessExpiresAt - Date.now() < 60_000) {
      const next = await this.refresh(s);
      if (!next) throw new ApiError(401, 'Your session expired. Please sign in again.');
      return next;
    }
    return s;
  }

  private async send(req: ApiRequest, accept: string): Promise<Response> {
    let s = await this.session();
    const exec = (token: string) =>
      this.fetchImpl(buildUrl(s.baseUrl, req.path, req.query), {
        method: req.method,
        headers: {
          ...this.extraHeaders,
          Authorization: `Bearer ${token}`,
          Accept: accept,
          ...(req.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
      });
    let res = await exec(s.accessToken);
    if (res.status === 401) {
      const next = await this.refresh(s);
      if (!next) throw new ApiError(401, 'Your session expired. Please sign in again.');
      s = next;
      res = await exec(s.accessToken);
    }
    return res;
  }

  async request<T>(req: ApiRequest): Promise<T> {
    const res = await this.send(req, 'application/json');
    if (!res.ok) throw await errorFrom(res);
    switch (req.responseType ?? 'json') {
      case 'none':
        return undefined as T;
      case 'text':
        return (await res.text()) as T;
      case 'base64':
        return toBase64(new Uint8Array(await res.arrayBuffer())) as T;
      default: {
        if (res.status === 204) return undefined as T;
        const text = await res.text();
        return (text ? JSON.parse(text) : undefined) as T;
      }
    }
  }

  async stream(req: ApiRequest, onEvent: StreamEventHandler, signal?: AbortSignal): Promise<void> {
    const s = await this.session();
    const res = await this.fetchImpl(buildUrl(s.baseUrl, req.path, req.query), {
      method: req.method,
      headers: { ...this.extraHeaders, Authorization: `Bearer ${s.accessToken}`, Accept: 'text/event-stream', 'Content-Type': 'application/json' },
      body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
      signal,
    });
    if (!res.ok) throw await errorFrom(res);
    if (!res.body) throw new ApiError(500, 'Streaming is not supported by this runtime.');
    await readSse(res.body, onEvent);
  }
}

/** In-memory store (tests, previews). */
export class MemorySessionStore implements SessionStore {
  private value: Session | null;
  constructor(initial: Session | null = null) {
    this.value = initial;
  }
  async load() {
    return this.value;
  }
  async save(s: Session | null) {
    this.value = s;
  }
}
